// ============================================
// API SERVICE - Centralized API calls
// ============================================

import {
  Admin,
  ApiListResponse,
  ApiResponse,
  AuthResponse,
  Booking,
  BookingFilters,
  BookingStatus,
  ChatConversation,
  ChatFilters,
  ChatMessage,
  CreateBookingRequest,
  CreateReviewRequest,
  CreateVehicleRequest,
  LoginRequest,
  RegisterRequest,
  Review,
  ReviewFilters,
  SendMessageRequest,
  StartConversationRequest,
  UpdateBookingRequest,
  UpdateReviewRequest,
  UpdateVehicleRequest,
  Vehicle,
  VehicleFilters,
} from './types';

const API_BASE = '/api';

// ============================================
// SESSION EXPIRATION EVENT
// ============================================

export const SESSION_EXPIRED_EVENT = 'session-expired';

export function emitSessionExpired() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function getAuthHeaders(): HeadersInit {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function getAuthHeaderOnly(): HeadersInit {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  return {
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok) {
    // Check for session expiration (401 Unauthorized)
    if (response.status === 401) {
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      // Only emit session expired if user was previously authenticated
      if (token) {
        emitSessionExpired();
      }
    }
    throw new Error(data.error || 'An error occurred');
  }
  return data;
}

function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const filtered = Object.entries(params).filter(([, value]) => value !== undefined);
  if (filtered.length === 0) return '';
  return (
    '?' + filtered.map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`).join('&')
  );
}

// ============================================
// ADMIN AUTH API
// ============================================

export const adminApi = {
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE}/admin/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<AuthResponse>(response);
  },

  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<AuthResponse>(response);
  },

  getProfile: async (): Promise<ApiResponse<Admin>> => {
    const response = await fetch(`${API_BASE}/admin/profile`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ApiResponse<Admin>>(response);
  },

  updateProfile: async (data: {
    fullName?: string;
    password?: string;
  }): Promise<ApiResponse<Admin>> => {
    const response = await fetch(`${API_BASE}/admin/profile`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ApiResponse<Admin>>(response);
  },
};

// ============================================
// VEHICLES API
// ============================================

export const vehiclesApi = {
  getAll: async (filters?: VehicleFilters): Promise<ApiListResponse<Vehicle>> => {
    const query = buildQueryString({ category: filters?.category });
    const response = await fetch(`${API_BASE}/vehicles${query}`);
    return handleResponse<ApiListResponse<Vehicle>>(response);
  },

  getById: async (id: number): Promise<ApiResponse<Vehicle>> => {
    const response = await fetch(`${API_BASE}/vehicles/${id}`);
    return handleResponse<ApiResponse<Vehicle>>(response);
  },

  create: async (data: CreateVehicleRequest): Promise<ApiResponse<Vehicle>> => {
    const response = await fetch(`${API_BASE}/vehicles`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ApiResponse<Vehicle>>(response);
  },

  update: async (id: number, data: UpdateVehicleRequest): Promise<ApiResponse<Vehicle>> => {
    const response = await fetch(`${API_BASE}/vehicles/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ApiResponse<Vehicle>>(response);
  },

  delete: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    const response = await fetch(`${API_BASE}/vehicles/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse<ApiResponse<{ message: string }>>(response);
  },

  reorder: async (vehicleIds: number[]): Promise<ApiResponse<{ message: string }>> => {
    const response = await fetch(`${API_BASE}/vehicles/reorder`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ vehicleIds }),
    });
    return handleResponse<ApiResponse<{ message: string }>>(response);
  },

  getAvailability: async (
    id: number
  ): Promise<
    ApiResponse<{
      vehicleId: number;
      vehicleName: string;
      bookedDates: Array<{
        id: number;
        startDate: string;
        endDate: string;
        status: string;
      }>;
    }>
  > => {
    const response = await fetch(`${API_BASE}/vehicles/${id}/availability`);
    return handleResponse(response);
  },
};

// ============================================
// UPLOADS API
// ============================================

export const uploadsApi = {
  uploadVehicleImages: async (files: File[]): Promise<ApiResponse<{ urls: string[] }>> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const response = await fetch(`${API_BASE}/uploads/vehicles`, {
      method: 'POST',
      headers: getAuthHeaderOnly(),
      body: formData,
    });

    return handleResponse<ApiResponse<{ urls: string[] }>>(response);
  },
};

// ============================================
// BOOKINGS API
// ============================================

export const bookingsApi = {
  getAll: async (filters?: BookingFilters): Promise<ApiListResponse<Booking>> => {
    const query = buildQueryString({
      status: filters?.status,
      vehicleId: filters?.vehicleId,
    });
    const response = await fetch(`${API_BASE}/bookings${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ApiListResponse<Booking>>(response);
  },

  getById: async (id: number): Promise<ApiResponse<Booking>> => {
    const response = await fetch(`${API_BASE}/bookings/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ApiResponse<Booking>>(response);
  },

  create: async (data: CreateBookingRequest): Promise<ApiResponse<Booking>> => {
    const response = await fetch(`${API_BASE}/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<ApiResponse<Booking>>(response);
  },

  update: async (id: number, data: UpdateBookingRequest): Promise<ApiResponse<Booking>> => {
    const response = await fetch(`${API_BASE}/bookings/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ApiResponse<Booking>>(response);
  },

  updateStatus: async (id: number, status: BookingStatus): Promise<ApiResponse<Booking>> => {
    const response = await fetch(`${API_BASE}/bookings/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    return handleResponse<ApiResponse<Booking>>(response);
  },

  delete: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    const response = await fetch(`${API_BASE}/bookings/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse<ApiResponse<{ message: string }>>(response);
  },
};

// ============================================
// CHAT API
// ============================================

export const chatApi = {
  getConversations: async (filters?: ChatFilters): Promise<ApiListResponse<ChatConversation>> => {
    const query = buildQueryString({
      status: filters?.status,
      limit: filters?.limit,
      offset: filters?.offset,
      since: filters?.since,
    });
    const response = await fetch(`${API_BASE}/chat/conversations${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ApiListResponse<ChatConversation>>(response);
  },

  getConversation: async (id: number): Promise<ApiResponse<ChatConversation>> => {
    const response = await fetch(`${API_BASE}/chat/conversations/${id}`);
    return handleResponse<ApiResponse<ChatConversation>>(response);
  },

  startConversation: async (
    data: StartConversationRequest
  ): Promise<ApiResponse<ChatConversation>> => {
    const response = await fetch(`${API_BASE}/chat/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<ApiResponse<ChatConversation>>(response);
  },

  updateConversation: async (
    id: number,
    status: 'open' | 'closed'
  ): Promise<ApiResponse<ChatConversation>> => {
    const response = await fetch(`${API_BASE}/chat/conversations/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    return handleResponse<ApiResponse<ChatConversation>>(response);
  },

  deleteConversation: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    const response = await fetch(`${API_BASE}/chat/conversations/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse<ApiResponse<{ message: string }>>(response);
  },

  sendMessage: async (
    conversationId: number,
    data: SendMessageRequest
  ): Promise<ApiResponse<ChatMessage>> => {
    const headers = data.isAdmin ? getAuthHeaders() : { 'Content-Type': 'application/json' };
    const response = await fetch(`${API_BASE}/chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    return handleResponse<ApiResponse<ChatMessage>>(response);
  },

  markAsRead: async (conversationId: number): Promise<ApiResponse<{ count: number }>> => {
    const response = await fetch(`${API_BASE}/chat/conversations/${conversationId}/messages`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    });
    return handleResponse<ApiResponse<{ count: number }>>(response);
  },
};

// ============================================
// REVIEWS API
// ============================================

export const reviewsApi = {
  getAll: async (filters?: ReviewFilters): Promise<ApiListResponse<Review>> => {
    const query = buildQueryString({ featured: filters?.featured ? 'true' : undefined });
    const response = await fetch(`${API_BASE}/reviews${query}`);
    return handleResponse<ApiListResponse<Review>>(response);
  },

  getById: async (id: number): Promise<ApiResponse<Review>> => {
    const response = await fetch(`${API_BASE}/reviews/${id}`);
    return handleResponse<ApiResponse<Review>>(response);
  },

  create: async (data: CreateReviewRequest): Promise<ApiResponse<Review>> => {
    const response = await fetch(`${API_BASE}/reviews`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ApiResponse<Review>>(response);
  },

  update: async (id: number, data: UpdateReviewRequest): Promise<ApiResponse<Review>> => {
    const response = await fetch(`${API_BASE}/reviews/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ApiResponse<Review>>(response);
  },

  delete: async (id: number): Promise<ApiResponse<{ message: string }>> => {
    const response = await fetch(`${API_BASE}/reviews/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse<ApiResponse<{ message: string }>>(response);
  },
};
